import { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useContext, useMemo, useEffect } from 'react'

import { useIsomorphicLayoutEffect } from '@unlikelystudio/react-hooks'

import Consent from '~/components/Consent'
import Page from '~/components/Page'
import UnSupported from '~/components/UnSupported'

import AppProviders from '~/providers/AppProviders'

import useCursorLoading from '~/hooks/useCursorLoading'

import { load, page } from '~/utils/analytics'
import isApple from '~/utils/is-apple'
import isIE from '~/utils/is-ie'
import waitBeforeTransition from '~/utils/wait-before-transition'

import '../styles/globals.scss'

const matchAll = require('string.prototype.matchall')
String.prototype.matchAll = matchAll

waitBeforeTransition()

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter()
  const [key] = router?.asPath?.split('?')

  useCursorLoading()

  // Segment pageview on route change
  useEffect(() => {
    page(router.asPath, {
      path: router.asPath,
      title: pageProps?.meta?.title,
      url: window.location.href,
    })
  }, [router.asPath])

  useIsomorphicLayoutEffect(() => {
    const body = document.querySelector('body')
    body.classList.toggle('ie', isIE)
    body.classList.toggle('apple', isApple)
  }, [isIE, isApple])

  // Set theme color from prismic data
  const themeColors = useMemo(
    () =>
      `body {
        --primary-color: ${pageProps?.globalData?.pagePrimaryTheme};
        --secondary-color: ${pageProps?.globalData?.pageSecondaryTheme};
      }`,
    [pageProps?.globalData],
  )

  return (
    <AppProviders pageProps={pageProps}>
      <Head>
        <style>{`body, html { width: 100%; }`}</style>
        {themeColors && <style>{themeColors}</style>}
      </Head>
      <Consent {...pageProps?.global?.consent} />
      {isIE && <UnSupported {...pageProps?.global?.unsupported} />}
      <Page {...pageProps} key={key}>
        <Component {...pageProps} key={key} />
      </Page>
    </AppProviders>
  )
}

export default MyApp
